/**
 * BookingDetailModal — Pure channel/payout predicates and helpers
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 * Phase 2 extracted from BookingDetailModal.jsx; behavior is identical (same inputs → same outputs).
 *
 * Purpose:
 *   Pure functions used by BookingDetailModal for canonical/persisted selection and channel-type
 *   detection (forced lease, owner stay, VRBO iCal/pre-stay/Stripe, CLC Lease, BookingEngine/Direct/HomeAway).
 *   No DOM, no fetch, no side effects.
 *
 * Assumptions & Invariants:
 *   - Pure functions only; no imports from admin.
 *   - detailData/booking shapes match owner reservation detail and list payloads.
 *
 * Dependencies: none
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 2 extracted from BookingDetailModal (verbatim logic).
 */

/**
 * Resolve canonical financial summary. Detail endpoint preferred; list (booking) used when modal opens before detail fetch.
 * @param {{ canonical_financial_summary?: object } | null} detailData
 * @param {{ canonical_financial_summary?: object } | null} booking
 * @returns {object | null}
 */
export function resolveCanonical(detailData, booking) {
  return detailData?.canonical_financial_summary || booking?.canonical_financial_summary;
}

/**
 * Resolve persisted-available flag and financials_persisted_summary. Used for folio source-of-truth; no fallback to canonical for numbers.
 * @param {{ persisted_available?: boolean, financials_persisted_summary?: object } | null} detailData
 * @returns {{ persistedAvailable: boolean, persistedSummary: object | null }}
 */
export function resolvePersisted(detailData) {
  return {
    persistedAvailable: detailData?.persisted_available ?? false,
    persistedSummary: detailData?.financials_persisted_summary ?? null,
  };
}

/**
 * Forced lease: channel_subtype clc_lease OR payout_method clc_lease_computed OR (master_channel_key clc AND channel_subtype clc_lease).
 * Used for status badge and OwnerStay precedence.
 * @param {object | null} canonical
 * @returns {boolean}
 */
export function isForcedLease(canonical) {
  return (
    canonical?.channel_subtype === 'clc_lease' ||
    canonical?.payout_method === 'clc_lease_computed' ||
    (canonical?.master_channel_key === 'clc' && canonical?.channel_subtype === 'clc_lease')
  );
}

/**
 * Owner stay only when NOT forced lease AND (canonical indicates owner OR channel_subtype owner_stay OR raw status ownerstay/owner_stay).
 * Raw-status fallback only when canonical is missing.
 * @param {object | null} canonical
 * @param {{ status?: string } | null} booking
 * @param {{ reservation?: { status?: string } } | null} detailData
 * @returns {boolean}
 */
export function isOwnerStay(canonical, booking, detailData) {
  if (isForcedLease(canonical)) return false;
  if (canonical?.master_channel_key === 'owner' || canonical?.channel_subtype === 'owner_stay') return true;
  if (!canonical) {
    const rawStatus = booking?.status || detailData?.reservation?.status || '';
    const normalizedStatus = rawStatus.toLowerCase().trim();
    return normalizedStatus === 'ownerstay' || normalizedStatus === 'owner_stay';
  }
  return false;
}

/**
 * Legacy VRBO iCal computed payout.
 * @param {object | null} canonical
 * @returns {boolean}
 */
export function isLegacyVRBOiCal(canonical) {
  return canonical?.payout_method === 'legacy_vrbo_ical_computed';
}

/**
 * VRBO non-iCal computed (pre-stay).
 * @param {object | null} canonical
 * @returns {boolean}
 */
export function isVRBONonICalComputed(canonical) {
  return canonical?.payout_method === 'crissy_computed_pre_stay' && canonical?.master_channel_key === 'vrbo';
}

/**
 * VRBO Stripe (hostaway net deposit + stripe subtype).
 * @param {object | null} canonical
 * @returns {boolean}
 */
export function isVRBOStripe(canonical) {
  return canonical?.payout_method === 'hostaway_net_deposit' && canonical?.channel_subtype === 'stripe';
}

/**
 * CLC Lease (payout_method or channel_subtype).
 * @param {object | null} canonical
 * @returns {boolean}
 */
export function isCLCLease(canonical) {
  return canonical?.payout_method === 'clc_lease_computed' || canonical?.channel_subtype === 'clc_lease';
}

/**
 * BookingEngine or direct_booking_engine.
 * @param {object | null} canonical
 * @returns {boolean}
 */
export function isBookingEngine(canonical) {
  return canonical?.channel_key === 'bookingengine' || canonical?.channel_subtype === 'direct_booking_engine';
}

/**
 * Direct channel.
 * @param {object | null} canonical
 * @returns {boolean}
 */
export function isDirect(canonical) {
  return canonical?.channel_key === 'direct' || canonical?.channel_subtype === 'direct';
}

/**
 * HomeAway channel.
 * @param {object | null} canonical
 * @returns {boolean}
 */
export function isHomeAway(canonical) {
  return canonical?.channel_key === 'homeaway' || canonical?.channel_subtype === 'homeaway';
}

/**
 * Lease (CLC lease) for deposit/tourist-tax logic: payout_method clc_lease_computed AND master_channel_key clc.
 * Stricter than isCLCLease for some folio branches.
 * @param {object | null} canonical
 * @returns {boolean}
 */
export function isLeaseStrict(canonical) {
  return canonical?.payout_method === 'clc_lease_computed' && canonical?.master_channel_key === 'clc';
}

/**
 * CLC master channel (non-lease check). Used for processing fees.
 * @param {object | null} canonical
 * @returns {boolean}
 */
export function isCLC(canonical) {
  return canonical?.master_channel_key === 'clc';
}

/**
 * Days until check-in from today. Uses date-only comparison (local calendar dates).
 * Use reservation.check_in || reservation.checkin_date; supports YYYY-MM-DD or ISO strings.
 * @param {string | null} checkIn - Check-in date string (YYYY-MM-DD or ISO)
 * @returns {number | null} - Whole days until check-in, or null if unparseable
 */
export function getDaysUntilCheckIn(checkIn) {
  if (!checkIn || typeof checkIn !== 'string') return null;
  const m = checkIn.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mon = parseInt(m[2], 10) - 1;
  const d = parseInt(m[3], 10);
  if (isNaN(y) || isNaN(mon) || isNaN(d)) return null;
  const checkInDate = new Date(y, mon, d);
  const today = new Date();
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const checkInOnly = new Date(checkInDate.getFullYear(), checkInDate.getMonth(), checkInDate.getDate());
  const diffMs = checkInOnly - todayDate;
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  return diffDays;
}

/**
 * All channel-type flags derived from canonical (and booking/detailData for isOwnerStay).
 * Same semantics as inline derivations in BookingDetailModal; use for folio and tooltips.
 * @param {object | null} canonical
 * @param {{ status?: string } | null} booking
 * @param {{ reservation?: { status?: string } } | null} detailData
 * @returns {{ isForcedLease: boolean, isOwnerStay: boolean, isLegacyVRBOiCal: boolean, isVRBONonICalComputed: boolean, isVRBOStripe: boolean, isCLCLease: boolean, isBookingEngine: boolean, isDirect: boolean, isHomeAway: boolean, isLeaseStrict: boolean }}
 */
export function deriveChannelFlags(canonical, booking, detailData) {
  return {
    isForcedLease: isForcedLease(canonical),
    isOwnerStay: isOwnerStay(canonical, booking, detailData),
    isLegacyVRBOiCal: isLegacyVRBOiCal(canonical),
    isVRBONonICalComputed: isVRBONonICalComputed(canonical),
    isVRBOStripe: isVRBOStripe(canonical),
    isCLCLease: isCLCLease(canonical),
    isBookingEngine: isBookingEngine(canonical),
    isDirect: isDirect(canonical),
    isHomeAway: isHomeAway(canonical),
    isLeaseStrict: isLeaseStrict(canonical),
  };
}

/**
 * Normalize booking for BookingDetailModal so listing_id is always set when determinable.
 * Required for Files/Links panel to load (/api/v1/owner/links needs listing_id).
 * Precedence: booking.listing_id → booking.hostaway_listing_id → booking.listingId → contextListingId.
 * @param {object | null} booking - Raw booking from calendar, reservations list, or detail fetch
 * @param {string | number | null | undefined} contextListingId - Page context (e.g. selectedProperty?.listing_id)
 * @returns {object | null} New object with ...booking and listing_id set when determinable; null if booking is null
 */
export function normalizeBookingForModal(booking, contextListingId) {
  if (!booking || typeof booking !== 'object') return booking;
  const resolved =
    booking.listing_id ??
    booking.hostaway_listing_id ??
    booking.listingId ??
    contextListingId;
  if (resolved == null || resolved === '') return { ...booking };
  const listingId =
    typeof resolved === 'number' && !Number.isNaN(resolved) && resolved > 0
      ? resolved
      : typeof resolved === 'string' && resolved.trim() !== ''
        ? (() => {
            const n = parseInt(resolved, 10);
            return !Number.isNaN(n) && n > 0 ? n : resolved.trim();
          })()
        : resolved;
  return { ...booking, listing_id: listingId };
}
